<template>
  <div>
    <div
      :class="hasQuotes ? 'quotes-page__wrapper' : 'no-quotes-page__wrapper'"
    >
      <QuoteSummary
        :cover="getTenantsContentCover"
        @click:backButton="back"
        @click="customiseQuote"
        handle-back-btn-externally
        heading="Your Tenants Contents Insurance quotes"
      />
      <div>
        <TenantsNoQuoteResultPage
          v-if="!hasQuotes"
          @back="back"
        ></TenantsNoQuoteResultPage>
      </div>
      <div v-if="hasQuotes">
        <div class="d-flex flex-wrap align-center mt-6 quote-actions">
          <div
            class="d-flex flex-column flex-lg-row flex-wrap gc-5 gr-2"
            v-if="isShowPremiumFlex"
          >
            <div class="d-flex align-center gc-2">
              <h3 class="d-none d-md-block font-20">Premium flex</h3>
              <h3 class="d-md-none font-20">Flex</h3>
              <BaseTooltip
                :text="TOOL_TIP_TEXT.PremiumFlex"
                icon="mdi-help-circle"
              />
            </div>

            <FlexSpinner
              :initialVal="commissionSacrifice"
              :maxCommissionSacrifice="getMaxCommissionSacrifice"
              @input="calculateFlex"
              class="font-18"
              type="percentage"
            />
          </div>
          <div
            class="d-flex flex-column align-sm-end flex-md-row ml-auto order-md-3"
          >
            <BaseButton
              @click="shareAllQuotes()"
              class="font-weight-bold px-1 py-3 d-flex align-center mr-xl-9"
              color="#106175"
              depressed
              flat
              icon="mdi-share-variant"
              ripple
              small
              :text="$vuetify.breakpoint.lgAndUp ? 'Share all' : 'Share'"
            />
            <BaseButton
              @click="printAllQuotes()"
              class="font-weight-bold px-1 py-3 d-flex align-center mt-1 mt-md-0"
              color="#106175"
              depressed
              flat
              icon="mdi-printer"
              ripple
              small
              :text="$vuetify.breakpoint.lgAndUp ? 'Print all' : 'Print'"
            />
          </div>
        </div>

        <TheQuotes
          @click:applyBtn="applyForQuote"
          @printQuotes="printQuote"
          @setAd="setAd"
          @shareQuote="shareQuote"
          @showPremiumBreakdownMonthly="showPremiumBreakdownMonthlyCustomTitle"
          @showWhatsIncluded="showWhatsIncluded"
          handleApplyBtnExternally
          accidentalDamageQuestion="Upgrade your accidental damage?"
          contentsAdName="Upgraded Contents AD"
          :payMonthlyAlert="false"
          :isHideAnnuallyPrice="true"
        />
      </div>
      <DrawerCustomiseCover
        v-model="isShowDrawerCustomiseCover"
        :cover="getTenantsContentCover"
        :additionalCover="ADD_ONS_TENANT_CONTENTS"
        :additionalCoverAnnualCost="ADD_ONS_ANNUAL_COST_TENANT_CONTENTS"
        :valuables="VALUABLES_TENANT"
        :personalPossessions="PERSONAL_POSSESSIONS_TENANT"
        :unspecifiedPersonalPossessions="
          UNSPECIFIED_PERSONAL_POSSESSIONS_TENANT
        "
        :contentCoverLabel="'What amount of contents cover is required?'"
        :contentsCoverDialogComponent="'DialogTenantContentContentInsurance'"
        @refreshCover="refreshCover"
        :productGroup="productGroup"
        :hintTextContentCoverRequired="'This Tenant’s Contents Insurance will cover everyone in your household. The total amount of cover must be sufficient to cover all your household goods, valuables and personal belongings.'"
        :hintTextContentCoverExcess="'The excess is the pre-agreed amount of money you\'ll need to pay towards any claims you make, The amount can impact the price you pay. Please check this amount is affordable for you.'"
        :lableContentCoverExcess="'What amount of excess would you like to pay?'"
        :configExcessExternal="configExcess"
      ></DrawerCustomiseCover>

      <BadgeDefaqto :show="isShowBadgeDefaqto" />

      <BaseDialog
        headerIcon="mdi-share-variant"
        headerIconColor="#106175"
        ref="shareDialog"
      >
        <template v-slot:content="{ data }">
          <DialogContentShareQuotes
            :content="data?.content"
            :quoteId="data?.quoteId"
            :quoteRequestId="data?.quoteRequestId"
            @success="shareQuotesSuccess"
          ></DialogContentShareQuotes>
        </template>
      </BaseDialog>

      <BaseDialog
        headerClass="pa-4"
        ref="DialogContentPremiumBreakdown"
        windowClass="pa-0 pa-xl-4 pb-xl-0"
      >
        <template v-slot:content="{ data }">
          <DialogContentPremiumBreakdown
            :breakdown-model="data"
            :legalExpensesTitle="'Tenant’s legal expenses cover (ex IPT)'"
            :cancellingYourPolicyDescription="'You have the right to cancel your policy within the 14 day cooling off period. After the 14 day cooling off period, you will still have the right to cancel your policy, however you will be charged for the cost of your cover on a pro rata daily basis.'"
          ></DialogContentPremiumBreakdown>
        </template>
      </BaseDialog>

      <BaseDialog
        contentClass="mb-0"
        headerClass="px-7 pt-6 pb-3"
        headerDivider
        headerIcon="mdi-printer"
        headerIconColor="#106175"
        ref="printQuotes"
        windowClass="pa-0 pb-xl-0 mb-0"
      >
        <template v-slot:content="{ data }">
          <DialogContentPrintQuotes :data="data"></DialogContentPrintQuotes>
        </template>
      </BaseDialog>

      <v-overlay :value="overlay">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
    </div>
  </div>
</template>

<script>
import { createConfigContentCover } from "@/common/configHelpers";
import { mapState, mapActions, mapGetters, mapMutations } from "vuex";
import { QUESTIONS_MODULE, DIALOG_MODULE } from "@/store/types/module-types";
import { TOOL_TIP_TEXT } from "@/constants/common.constants.js";
import PremiumBreakdownMixin from "@/Mixins/PremiumBreakdownMixin";
import { adBoolToString, adStringToBool } from "@/common/cover";
import { ROUTE_NAME } from "@/constants/route.constants";
import { getProductGroup } from "@/common/question-set";
import {
  ADD_ONS_TENANT_CONTENTS,
  ADD_ONS_ANNUAL_COST_TENANT_CONTENTS,
} from "@/constants/additional-cover-options";
import { VALUABLES_TENANT } from "@/constants/valuable-options";
import {
  PERSONAL_POSSESSIONS_TENANT,
  UNSPECIFIED_PERSONAL_POSSESSIONS_TENANT,
} from "@/constants/possessions-options";

export default {
  name: "YourQuotesPage",
  components: {
    BaseButton: () => import("@/components/BaseComponents/BaseButton.vue"),
    FlexSpinner: () => import("@/components/QuoteResult/FlexSpinner.vue"),
    QuoteSummary: () => import("@/components/QuoteResult/QuoteSummary.vue"),
    BaseTooltip: () => import("@/components/BaseComponents/BaseTooltip.vue"),
    TheQuotes: () => import("@/components/QuoteResult/TheQuotes.vue"),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DialogContentShareQuotes: () =>
      import("@/components/QuoteResult/DialogContentShareQuotes.vue"),
    DialogContentPrintQuotes: () =>
      import("@/components/QuoteResult/DialogContentPrintQuotes.vue"),
    DialogContentPremiumBreakdown: () =>
      import("@/components/QuoteResult/DialogContentPremiumBreakdown.vue"),
    BadgeDefaqto: () => import("@/components/QuoteResult/BadgeDefaqto.vue"),
    DrawerCustomiseCover: () =>
      import("@/components/QuoteResult/DrawerCustomiseCover.vue"),
    TenantsNoQuoteResultPage: () =>
      import("@/views/Tenants/B2B/TenantsContents/NoQuotePage.vue"),
  },
  mixins: [PremiumBreakdownMixin],
  data() {
    return {
      isShowDrawerCustomiseCover: false,
      isShowBadgeDefaqto: true,
      TOOL_TIP_TEXT,
      ADD_ONS_TENANT_CONTENTS,
      ADD_ONS_ANNUAL_COST_TENANT_CONTENTS,
      VALUABLES_TENANT,
      PERSONAL_POSSESSIONS_TENANT,
      UNSPECIFIED_PERSONAL_POSSESSIONS_TENANT,
      configExcess: createConfigContentCover(50, 500, 0),
    };
  },
  watch: {
    isShowDrawerCustomiseCover(val) {
      if (!val) {
        this.isShowBadgeDefaqto = true;
      }
    },
  },
  computed: {
    ...mapGetters(QUESTIONS_MODULE, [
      "coverSelection",
      "getMaxCommissionSacrifice",
      "getQuoteRequestId",
      "getTenantsContentCover",
      "hasQuotes",
      "isShowPremiumFlex",
      "qsId",
    ]),
    ...mapState(QUESTIONS_MODULE, [
      "commissionSacrifice",
      "overlay",
      "quoteResponse",
    ]),
    productGroup() {
      return getProductGroup(this.qsId);
    },
    selectedAds() {
      return {
        contentsAd: adBoolToString(this.coverSelection.contentsAd),
      };
    },
  },
  mounted() {
    // Restore accidental damage selections
    this.setCoverSelection({
      contentsAd: adStringToBool(
        this.quoteResponse?.CoverSelections?.ContentsAD
      ),
    });
    // Can't go forward automatically from this page
    this.setRestoringLastStep(false);
  },
  methods: {
    ...mapActions(QUESTIONS_MODULE, [
      "getDataToPrintAllQuotes",
      "getDataToPrintQuote",
      "getDocuments",
      "saveQuoteResults",
      "setCoverSelection",
      "setOverlayVisible",
      "setRestoringLastStep",
      "updateCommissionSacrifice",
      "refreshSessionAndNextStep",
    ]),
    ...mapMutations(DIALOG_MODULE, ["showModal"]),
    showPremiumBreakdownMonthlyCustomTitle(quote, productGroup) {
      const customTitle = "Monthly Premium Breakdown";
      this.showPremiumBreakdownMonthly(quote, productGroup, customTitle);
    },
    back() {
      this.$router.push({
        name: ROUTE_NAME.TENANTS_CONTENTS.B2B.PROPERTY_DETAILS,
      });
    },
    refreshCover(addedContentsAd) {
      this.setCoverSelection({
        contentsAd: addedContentsAd,
      });
    },
    customiseQuote() {
      this.isShowDrawerCustomiseCover = true;
    },
    calculateFlex(val) {
      this.updateCommissionSacrifice(val);
    },
    async shareAllQuotes() {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", {
        content: { ...this.getTenantsContentCover, ...this.selectedAds },
        quoteRequestId: this.getQuoteRequestId,
      });
    },
    async saveQuotesAndGetDocumentsAsync() {
      await this.saveQuoteResults();

      return await this.getDocuments();
    },
    async printAllQuotes() {
      try {
        this.setOverlayVisible(true);

        const docs = await this.saveQuotesAndGetDocumentsAsync();

        var data = await this.getDataToPrintAllQuotes({
          docs,
          ads: this.selectedAds,
        });
        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.setOverlayVisible(false);
      }
    },
    setAd(data) {
      this.setCoverSelection({
        contentsAd: data.contentsAd,
      });
    },
    applyForQuote() {
      this.refreshSessionAndNextStep(
        ROUTE_NAME.TENANTS_CONTENTS.B2B.APPLICATION
      );
    },
    async shareQuote(quoteId) {
      await this.saveQuoteResults();

      this.$refs.shareDialog.open("Share via email", {
        content: this.getTenantsContentCover,
        quoteId: quoteId,
      });
    },
    async printQuote(quote) {
      try {
        this.setOverlayVisible(true);

        const docs = await this.saveQuotesAndGetDocumentsAsync();

        var data = await this.getDataToPrintQuote({ docs, quote });

        this.$refs.printQuotes.open("Download for printing", data);
      } catch (error) {
        console.error(error);
      } finally {
        this.setOverlayVisible(false);
      }
    },
    shareQuotesSuccess() {
      this.$refs.shareDialog.cancel();
    },
    showWhatsIncluded() {
      this.showModal({
        dialogComponentName:
          this.ADD_ONS_TENANT_CONTENTS[1].dialogComponentName,
        customClass: "pb-10 dialog-contents__wrapper",
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.no-quotes-page__wrapper {
  :deep(.quote-summary__card) {
    box-shadow: none !important;
  }
}
@media screen and (max-width: ($grid-breakpoints-sm - 1)) {
  .additional-content {
    border-top: 1px solid #ccd6dd;
  }
}

@media screen and (max-width: $grid-breakpoints-lg) {
  .quote-actions {
    margin: 24px 16px 0px 16px;
  }
}
</style>
